import { Badge, Card, Group, SimpleGrid, Skeleton, Stack, Table, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { tripsApi, type DispatchOffer, type DispatchOutcome } from "../../api/trips";
import { DriverLink } from "../common/EntityLink";
import { QueryError } from "../common/QueryError";
import { formatDateTime } from "../../lib/format";

const OUTCOME: Record<DispatchOutcome, { label: string; color: string }> = {
  assigned: { label: "Driver assigned", color: "green" },
  searching: { label: "Still searching", color: "blue" },
  no_driver_found: { label: "No driver found", color: "red" },
  cancelled_before_assignment: { label: "Cancelled before assignment", color: "gray" },
};

const RESPONSE: Record<DispatchOffer["status"], { label: string; color: string }> = {
  accepted: { label: "Accepted", color: "green" },
  rejected: { label: "Rejected", color: "red" },
  timeout: { label: "No response", color: "yellow" },
  pending: { label: "Awaiting response", color: "blue" },
  unreachable: { label: "Not reachable", color: "gray" },
};

function duration(seconds: number | null | undefined): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text size="sm" fw={600}>
        {value}
      </Text>
    </Stack>
  );
}

/**
 * Everything that happened before a driver was assigned: when the rider asked,
 * which drivers were offered the trip (in order), how each answered and how
 * long they took.
 */
export function TripDispatchCard({ tripId }: { tripId: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trip-dispatch", tripId],
    queryFn: () => tripsApi.dispatch(tripId),
    enabled: !!tripId,
  });

  return (
    <Card withBorder radius="md" padding="lg" mb="md">
      <Group justify="space-between" mb="md">
        <Title order={5}>Dispatch</Title>
        {data && (
          <Badge color={OUTCOME[data.outcome].color} variant="light">
            {OUTCOME[data.outcome].label}
          </Badge>
        )}
      </Group>

      {isLoading ? (
        <Skeleton h={120} />
      ) : error || !data ? (
        <QueryError error={error ?? new Error("Not found")} onRetry={refetch} />
      ) : (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            <Stat label="Requested" value={formatDateTime(data.requestedAt)} />
            <Stat label="Drivers offered" value={data.summary.offered} />
            <Stat
              label="Accepted / rejected / no reply / unreachable"
              value={`${data.summary.accepted} / ${data.summary.rejected} / ${data.summary.timedOut} / ${data.summary.unreachable ?? 0}`}
            />
            <Stat label="Avg. response time" value={duration(data.summary.avgResponseSeconds)} />
            {data.assignedAt && (
              <>
                <Stat label="Assigned" value={formatDateTime(data.assignedAt)} />
                <Stat label="Time to assign" value={duration(data.secondsToAssign)} />
              </>
            )}
          </SimpleGrid>

          {data.offers.length ? (
            <Table.ScrollContainer minWidth={640}>
              <Table verticalSpacing="xs" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>#</Table.Th>
                    <Table.Th>Driver</Table.Th>
                    <Table.Th>Offered at</Table.Th>
                    <Table.Th>Response</Table.Th>
                    <Table.Th>Responded in</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.offers.map((o) => (
                    <Table.Tr key={`${o.order}-${o.driverId}`}>
                      <Table.Td>{o.order}</Table.Td>
                      <Table.Td>
                        <DriverLink driver={o.driverId ?? undefined} name={o.name ?? undefined} />
                        {(o.vehicle || o.plate) && (
                          <Text size="xs" c="dimmed">
                            {[o.vehicle, o.plate].filter(Boolean).join(" · ")}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{formatDateTime(o.sentAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        {o.removedByAdmin ? (
                          <Badge color="gray" variant="light">
                            Removed by admin
                          </Badge>
                        ) : o.cancelledAfterAccept ? (
                          <Stack gap={2}>
                            <Badge color="orange" variant="light">
                              Cancelled after accepting
                            </Badge>
                            {o.cancelReason && (
                              <Text size="xs" c="dimmed">
                                {o.cancelReason}
                              </Text>
                            )}
                          </Stack>
                        ) : (
                          <Badge color={RESPONSE[o.status].color} variant="light">
                            {RESPONSE[o.status].label}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{duration(o.responseSeconds)}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <Text size="sm" c="dimmed">
              {data.outcome === "no_driver_found"
                ? "No drivers were available nearby, so the request was never offered to anyone."
                : "No driver has been offered this trip yet."}
            </Text>
          )}

          {data.cancel && !data.assignedAt && (
            <Text size="sm" c="dimmed">
              Cancelled {formatDateTime(data.cancel.at)}
              {data.cancel.by ? ` by ${data.cancel.by}` : ""}
              {data.cancel.reason ? ` — ${data.cancel.reason}` : ""}
            </Text>
          )}
        </Stack>
      )}
    </Card>
  );
}
